import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import type { TQuotationZoomDirection } from "store/viewer.store"
import { hexToRgb } from "services/annotations.service"
import { resolveViewerPanes } from "./viewerPanes"

export type TViewerTool = "select" | "pan" | "zoom"

// Every live viewport. Apryse's MultiViewer mode (REQ-399 split screen) runs a SECOND, fully
// independent DocumentViewer, and each one owns its own tool instances: `getTool(PAN)` on viewer 2
// is a different object than on viewer 1, with its own tool-mode map. The `??` fallback covers the
// single-pane case and test doubles that stub Core without the multi-viewer API.
const getViewers = (instance: WebViewerInstance) =>
	instance.Core.getDocumentViewers?.() ?? [instance.Core.documentViewer]

// Apply a tool to EVERY pane, resolving it by name from each viewer. Never pass one viewer's tool
// object to another — Apryse's own `core.setToolMode` and `syncDocumentViewers` re-resolve by name
// for exactly this reason, and a foreign tool object silently does nothing.
export const setToolModeOnAllPanes = (
	instance: WebViewerInstance,
	toolName: string | Core.Tools.ToolNames,
): void => {
	for (const documentViewer of getViewers(instance)) {
		documentViewer?.setToolMode(documentViewer.getTool(toolName))
	}
}

// How long to wait for a panel-driven re-layout to settle before the final tool restore. Same
// animation `preservePaneViewport` waits out, so the same figure.
const TOOL_RESTORE_SETTLE_MS = 350

// Snapshot every pane's CURRENT tool and return a function that puts each one back.
//
// Why this is needed: opening a panel makes Apryse's OWN UI layer reset the first viewer's tool —
// measured, it lands on `TextSelect` while viewer 2 stays on `AnnotationEdit`. No app code asks for
// it (wrapping `setToolMode` on both viewers logs nothing while it happens), and it only ever hits
// viewer 1, which is the same "UI.* is viewer-1 only" trap documented in AGENTS.md. Two things go
// wrong as a result, and neither announces itself:
//
//   • the outgoing tool's `switchOut` DESELECTS everything on the way out, which is what silently
//     emptied the selection after a smart navigation (#1311);
//   • the pane then STAYS on `TextSelect`, so drag-move and resize (REQ-350) are dead in it until
//     the user happens to press the Select tool — a recovery nobody would guess.
//
// Per-viewer, not one tool for all of them: the ruler deliberately runs DIFFERENT tools in the two
// panes (measure on pane 1, select on pane 2 — see toggleRulerTool), so restoring a single "current
// tool" everywhere would break it. Restores only where the tool actually changed, so it cannot
// fight a tool change the user made themselves during the settle window.
export const preservePaneToolModes = (instance: WebViewerInstance | null): (() => void) => {
	if (!instance) return () => {}
	const snapshot = getViewers(instance).map((documentViewer) => ({
		documentViewer,
		toolName: documentViewer?.getToolMode?.()?.name,
	}))

	const restore = () => {
		for (const { documentViewer, toolName } of snapshot) {
			if (!documentViewer || !toolName) continue
			if (documentViewer.getToolMode?.()?.name === toolName) continue
			const tool = documentViewer.getTool(toolName)
			if (tool) documentViewer.setToolMode(tool)
		}
	}

	return () => {
		requestAnimationFrame(restore)
		window.setTimeout(restore, TOOL_RESTORE_SETTLE_MS)
	}
}

export type TViewerFitMode = "Zoom" | "FitWidth" | "FitPage"

const getFitMode = (
	documentViewer: Core.DocumentViewer,
	fitMode: TViewerFitMode,
) => {
	if (fitMode === "Zoom") return documentViewer.FitMode.Zoom
	if (fitMode === "FitWidth") return documentViewer.FitMode.FitWidth
	return documentViewer.FitMode.FitPage
}

export const setFitModeOnAllPanes = (
	instance: WebViewerInstance,
	fitMode: TViewerFitMode,
): void => {
	for (const documentViewer of getViewers(instance)) {
		documentViewer.setFitMode(getFitMode(documentViewer, fitMode))
	}
}

export const zoomOnAllPanes = (
	instance: WebViewerInstance,
	nextZoom: number,
): void => {
	for (const { documentViewer, scrollEl } of resolveViewerPanes(instance)) {
		const currentZoom = documentViewer.getZoomLevel()
		const centerX = scrollEl.scrollLeft + scrollEl.clientWidth / 2
		const centerY = scrollEl.scrollTop + scrollEl.clientHeight / 2

		documentViewer.setFitMode(documentViewer.FitMode.Zoom)
		documentViewer.zoomTo(nextZoom)
		window.requestAnimationFrame(() => {
			const zoomRatio = currentZoom > 0 ? nextZoom / currentZoom : 1
			scrollEl.scrollLeft = centerX * zoomRatio - scrollEl.clientWidth / 2
			scrollEl.scrollTop = centerY * zoomRatio - scrollEl.clientHeight / 2
		})
	}
}

export const changeViewerTool = (
	instance: WebViewerInstance,
	tool: TViewerTool,
	setZoomDirection: (direction: TQuotationZoomDirection) => void,
	zoomDirection: TQuotationZoomDirection = "in",
): void => {
	const { Core } = instance
	if (tool !== "select") {
		// Primary manager only: the split pane's mirrored annotations hold no selection of their own,
		// it flows through the primary manager. See AnnotationService.registerSplitViewer.
		Core.annotationManager.deselectAllAnnotations()
	}
	if (tool === "select") {
		setToolModeOnAllPanes(instance, Core.Tools.ToolNames.EDIT)
	}
	if (tool === "pan") {
		setToolModeOnAllPanes(instance, Core.Tools.ToolNames.PAN)
	}
	if (tool === "zoom") {
		setZoomDirection(zoomDirection)
		setToolModeOnAllPanes(instance, Core.Tools.ToolNames.MARQUEE)
	}
}

// Seed Apryse's NATIVE FreeText create tool with the font size / text color the note tool-options
// bar currently shows, so a note is BORN at that size (REQ-395/396, PR #1386 review: "the note is
// still typed using a small font size and is re-rendered in its expected size only after the user
// clicks Enter").
//
// Why the tool and not just the annotation: Apryse opens its inline rich-text editor the instant the
// box is drawn, and that editor takes its size from the annotation AS CREATED. Our own styling runs
// later — `registerCreatedNoteAnnotation` can only fire once the create POST resolves — so without
// this the editor opens at the tool's stock 12pt and every keystroke is typed small until the edit
// commits and the annotation's real size takes over. Measured: with these defaults set to 72pt the
// annotation comes out of the drag already `FontSize: "72pt"`.
//
// `setStyles` MERGES, which matters here: the FreeText tool's own defaults carry
// `StrokeThickness: 0` (measured — that zero is exactly why a natively-drawn note has no frame,
// see `AnnotationService.applyNoteStyle`), so replacing `defaults` wholesale the way
// `useFigureHighlightCreate` does for Rectangle/Ellipse would drop it and give every fresh note the
// frame this same review asked us to remove. Feature-detected and best-effort: losing this costs the
// live preview only — the persisted note is styled from the backend record regardless.
export const applyNoteToolStyles = (
	instance: WebViewerInstance | null,
	style: { fontSize: number; textColor: string },
): void => {
	if (!instance) return
	try {
		const { documentViewer, Tools, Annotations } = instance.Core
		const tool = documentViewer.getTool(Tools.ToolNames.FREETEXT) as unknown as
			| { setStyles?: (styles: Record<string, unknown>) => void }
			| undefined
		if (typeof tool?.setStyles !== "function") return
		const [r, g, b] = hexToRgb(style.textColor)
		tool.setStyles({
			FontSize: `${style.fontSize}pt`,
			TextColor: new Annotations.Color(r, g, b, 1),
		})
	} catch (error) {
		console.warn("Could not preview the note font size/color on the create tool:", error)
	}
}

// Measuring is PRIMARY-PANE ONLY, by product decision. A measurement drawn in the split pane lands
// in the mirror annotation manager, which `syncSplitAnnotations()` rewrites on every geometry change
// and clears on split exit — the measurement would silently vanish. So the ruler toggles on pane 1
// while the split pane is put into the select tool, never left holding a stale mode.
export const toggleRulerTool = (instance: WebViewerInstance): void => {
	const { documentViewer, Tools } = instance.Core
	const measurementTool = documentViewer.getTool(Tools.ToolNames.DISTANCE_MEASUREMENT)

	if (documentViewer.getToolMode() === measurementTool) {
		setToolModeOnAllPanes(instance, Tools.ToolNames.EDIT)
		return
	}

	documentViewer.setToolMode(measurementTool)
	for (const splitViewer of getViewers(instance).slice(1)) {
		splitViewer?.setToolMode(splitViewer.getTool(Tools.ToolNames.EDIT))
	}
}
